"use client";

import { useEffect, useRef, useState } from "react";
import type { Testimonial } from "@/lib/testimonials";
import { stripHtml } from "@/lib/testimonials";
import { getAvatarGradient, getInitials } from "@/lib/testimonial-avatar";

interface TestimonialCardProps {
  testimonial: Testimonial;
}

export default function TestimonialCard({ testimonial }: TestimonialCardProps) {
  const excerpt = stripHtml(testimonial.description, 220);
  const fullText = stripHtml(testimonial.description);
  const isTruncated = fullText.length > excerpt.length;
  const [showOverlay, setShowOverlay] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!showOverlay) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setShowOverlay(false);
    }
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [showOverlay]);

  return (
    <>
      <div className="testi-page-card">
        {/* Decorative quote */}
        <div className="testi-page-card-quote">
          <i className="fa-solid fa-quote-left"></i>
        </div>

        {/* Stars */}
        <div className="testi-page-card-stars">
          <i className="fa-solid fa-star"></i>
          <i className="fa-solid fa-star"></i>
          <i className="fa-solid fa-star"></i>
          <i className="fa-solid fa-star"></i>
          <i className="fa-solid fa-star"></i>
        </div>

        {/* Comment body */}
        {testimonial.titleMoreDescription && (
          <h4 className="testi-page-card-heading">
            {testimonial.titleMoreDescription}
          </h4>
        )}
        <p className="testi-page-card-text">{excerpt}</p>

        {/* Read More button */}
        {isTruncated && (
          <button
            className="testi-page-card-readmore"
            onClick={() => setShowOverlay(true)}
          >
            Read More <i className="fa-solid fa-arrow-right"></i>
          </button>
        )}

        {/* Author footer */}
        <div className="testi-page-card-author">
          <div className="testi-page-card-avatar">
            {testimonial.photo ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={testimonial.photo} alt={testimonial.signature} loading="lazy" decoding="async" />
            ) : (
              <span
                className="testi-page-card-monogram"
                aria-hidden="true"
                style={{ background: getAvatarGradient(testimonial.signature) }}
              >
                {getInitials(testimonial.signature)}
              </span>
            )}
          </div>
          <div className="testi-page-card-info">
            <h5 className="testi-page-card-name">{testimonial.signature}</h5>
            <span className="testi-page-card-date">
              <i className="fa-solid fa-circle-check"></i>
              Verified Traveler
            </span>
          </div>
        </div>
      </div>

      {/* ── Full Detail Overlay ── */}
      {showOverlay && (
        <div
          className="testi-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowOverlay(false);
          }}
        >
          <div className="testi-overlay-content" ref={overlayRef}>
            {/* Close button */}
            <button
              className="testi-overlay-close"
              onClick={() => setShowOverlay(false)}
              aria-label="Close"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>

            {/* Header with gradient accent */}
            <div className="testi-overlay-header">
              <div className="testi-overlay-quote">
                <i className="fa-solid fa-quote-left"></i>
              </div>
              <div className="testi-overlay-stars">
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
                <i className="fa-solid fa-star"></i>
              </div>
            </div>

            {/* Full text */}
            {testimonial.titleMoreDescription && (
              <h3 className="testi-overlay-title">
                {testimonial.titleMoreDescription}
              </h3>
            )}
            <div
              className="testi-overlay-body"
              dangerouslySetInnerHTML={{ __html: testimonial.description }}
            />

            {/* Author */}
            <div className="testi-overlay-author">
              <div className="testi-page-card-avatar">
                {testimonial.photo ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={testimonial.photo} alt={testimonial.signature} loading="lazy" decoding="async" />
                ) : (
                  <span
                    className="testi-page-card-monogram"
                    aria-hidden="true"
                    style={{ background: getAvatarGradient(testimonial.signature) }}
                  >
                    {getInitials(testimonial.signature)}
                  </span>
                )}
              </div>
              <div>
                <h5 className="testi-page-card-name">
                  {testimonial.signature}
                </h5>
                <span className="testi-page-card-date">
                  <i className="fa-solid fa-circle-check"></i>
                  Verified Traveler
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
